import {
  FC,
  PropsWithChildren,
  useState,
  createContext,
  useContext,
  useCallback,
} from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { OrderTracking, Shipment } from '../../../__generated__/loma';

export type OrderTrackingQueryData = { orderTracking: OrderTracking };

export interface OrderTrackingProviderValue {
  orderTrackingData?: OrderTracking;
}

export interface OrderTrackingProviderProps {
  value: OrderTrackingProviderValue;
}

export interface OrderTrackingValue {
  orderTrackingData?: OrderTracking;
  getCurrentShipment: () => Shipment | null;
  setCurrentShipment: (id: string) => void;
  mainShipmentId?: string | null;
}

export const OrderTrackingContext = createContext<OrderTrackingValue>({
  orderTrackingData: undefined,
  getCurrentShipment: () => null,
  setCurrentShipment: () => {},
  mainShipmentId: null,
});

export const OrderTrackingProvider: FC<
  PropsWithChildren<OrderTrackingProviderProps>
> = ({ value: { orderTrackingData }, children }) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [mainShipmentId, setMainShipmentId] = useState<string | null>(
    searchParams?.get('main_shipment_id') ??
      orderTrackingData?.orders[0].mainShipmentId ??
      null,
  );

  const getCurrentShipment = useCallback(() => {
    const { shipments } = orderTrackingData?.orders[0] || {};

    if (!mainShipmentId || !shipments) return null;
    const shipment = shipments.find(
      ({ shipmentId }) => shipmentId === mainShipmentId,
    );
    return shipment || null;
  }, [orderTrackingData, mainShipmentId]);

  const setCurrentShipment = useCallback(
    (newMainShipmentId: string) => {
      const updatedParams = new URLSearchParams(searchParams?.toString());
      updatedParams.set('main_shipment_id', newMainShipmentId);
      const updatedUrl = `${pathname}?${updatedParams.toString()}`;
      router.replace(updatedUrl);

      setMainShipmentId(newMainShipmentId);
    },
    [pathname, router, searchParams],
  );

  return (
    <OrderTrackingContext.Provider
      value={{
        orderTrackingData,
        getCurrentShipment,
        setCurrentShipment,
        mainShipmentId,
      }}
    >
      {children}
    </OrderTrackingContext.Provider>
  );
};

export default () => useContext(OrderTrackingContext);
